// Imperative modal helpers, so screens read like the desktop code that calls QMessageBox / QInputDialog:
//   if (await confirm({ title, text })) { ... }
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { Dialog } from "./Dialog";

type Spec =
  | { kind: "alert"; title: string; text: ReactNode; resolve: () => void }
  | { kind: "confirm"; title: string; text: ReactNode; yes: string; no: string; danger?: boolean; resolve: (v: boolean) => void }
  | { kind: "choose3"; title: string; text: ReactNode; yes: string; no: string; resolve: (v: "yes" | "no" | "cancel") => void }
  | { kind: "prompt"; title: string; label: string; initial: string; resolve: (v: string | null) => void }
  | { kind: "select"; title: string; label: string; options: string[]; resolve: (v: string | null) => void };

export interface DialogApi {
  alert(o: { title: string; text: ReactNode }): Promise<void>;
  confirm(o: { title: string; text: ReactNode; yes?: string; no?: string; danger?: boolean }): Promise<boolean>;
  /** Yes / No / Cancel (QMessageBox with three buttons). */
  choose3(o: { title: string; text: ReactNode; yes: string; no: string }): Promise<"yes" | "no" | "cancel">;
  prompt(o: { title: string; label: string; initial?: string }): Promise<string | null>;
  select(o: { title: string; label: string; options: string[] }): Promise<string | null>;
}

const Ctx = createContext<DialogApi | null>(null);
export const useDialogs = (): DialogApi => {
  const c = useContext(Ctx);
  if (!c) throw new Error("useDialogs outside DialogProvider");
  return c;
};

export function DialogProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<Spec[]>([]);
  const push = useCallback(<T,>(make: (resolve: (v: T) => void) => Spec) => new Promise<T>((res) => setQueue((q) => [...q, make(res)])), []);
  const api = useMemo<DialogApi>(() => ({
    alert: (o) => push<void>((resolve) => ({ kind: "alert", ...o, resolve })),
    confirm: (o) => push<boolean>((resolve) => ({ kind: "confirm", yes: "Yes", no: "No", ...o, resolve })),
    choose3: (o) => push<"yes" | "no" | "cancel">((resolve) => ({ kind: "choose3", ...o, resolve })),
    prompt: (o) => push<string | null>((resolve) => ({ kind: "prompt", initial: "", ...o, resolve })),
    select: (o) => push<string | null>((resolve) => ({ kind: "select", ...o, resolve })),
  }), [push]);
  const done = () => setQueue((q) => q.slice(1));
  const cur = queue[0];
  return (
    <Ctx.Provider value={api}>
      {children}
      {cur && <Current key={queue.length + cur.kind + cur.title} spec={cur} done={done} />}
    </Ctx.Provider>
  );
}

function Current({ spec, done }: { spec: Spec; done: () => void }) {
  const [value, setValue] = useState(spec.kind === "prompt" ? spec.initial : spec.kind === "select" ? spec.options[0] ?? "" : "");
  const settled = useRef(false);
  const finish = (fn: () => void) => { if (settled.current) return; settled.current = true; fn(); done(); };

  switch (spec.kind) {
    case "alert":
      return (
        <Dialog title={spec.title} onClose={() => finish(spec.resolve)} testId="messagebox">
          <div style={{ whiteSpace: "pre-wrap", marginBottom: 8 }}>{spec.text}</div>
          <div className="dialog-actions"><button className="btn primary" onClick={() => finish(spec.resolve)}>OK</button></div>
        </Dialog>
      );
    case "confirm":
      return (
        <Dialog title={spec.title} onClose={() => finish(() => spec.resolve(false))} testId="messagebox">
          <div style={{ whiteSpace: "pre-wrap", marginBottom: 8 }}>{spec.text}</div>
          <div className="dialog-actions">
            <button className="btn" onClick={() => finish(() => spec.resolve(false))}>{spec.no}</button>
            <button className={`btn ${spec.danger ? "danger" : "primary"}`} onClick={() => finish(() => spec.resolve(true))}>{spec.yes}</button>
          </div>
        </Dialog>
      );
    case "choose3":
      return (
        <Dialog title={spec.title} onClose={() => finish(() => spec.resolve("cancel"))} testId="messagebox" width={480}>
          <div style={{ whiteSpace: "pre-wrap", marginBottom: 8 }}>{spec.text}</div>
          <div className="dialog-actions">
            <button className="btn" onClick={() => finish(() => spec.resolve("cancel"))}>Cancel</button>
            <button className="btn" onClick={() => finish(() => spec.resolve("no"))}>{spec.no}</button>
            <button className="btn primary" onClick={() => finish(() => spec.resolve("yes"))}>{spec.yes}</button>
          </div>
        </Dialog>
      );
    case "prompt":
      return (
        <Dialog title={spec.title} onClose={() => finish(() => spec.resolve(null))} testId="prompt">
          <label htmlFor="prompt-input">{spec.label}</label>
          <input id="prompt-input" className="input" style={{ marginTop: 6 }} value={value} autoFocus
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") finish(() => spec.resolve(value)); }} />
          <div className="dialog-actions">
            <button className="btn" onClick={() => finish(() => spec.resolve(null))}>Cancel</button>
            <button className="btn primary" onClick={() => finish(() => spec.resolve(value))}>OK</button>
          </div>
        </Dialog>
      );
    case "select":
      return (
        <Dialog title={spec.title} onClose={() => finish(() => spec.resolve(null))} testId="select-dialog">
          <label htmlFor="select-input">{spec.label}</label>
          <select id="select-input" className="select" style={{ marginTop: 6 }} value={value} onChange={(e) => setValue(e.target.value)}>
            {spec.options.map((o) => <option key={o}>{o}</option>)}
          </select>
          <div className="dialog-actions">
            <button className="btn" onClick={() => finish(() => spec.resolve(null))}>Cancel</button>
            <button className="btn primary" onClick={() => finish(() => spec.resolve(value))}>OK</button>
          </div>
        </Dialog>
      );
  }
}
